import { InfoCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import { useGetIdentity, useList } from "@refinedev/core";
import { Alert, Button, Card, Flex, Modal, Tooltip, Typography } from "antd";
import { Empty, message } from "antd/lib";
import { createNewPayment } from "api/credits";
import cancelIcon from "assets/cancel.png";
import checkIcon from "assets/checkIcon.png";
import { QUIX_SALES_EMAIL } from "constants/common";
import { useUserType } from "hooks/useUserType";
import { useState } from "react";
import { currencyFormat } from "utils";
import "./scroll.css";
import classes from "./styles.module.css";

import { CreditsCalculatorTab } from "./CreditsCalculatorTab";

type Bundle = {
  id: string;
  credit: number;
  amount: number;
  description: string;
};

type TabsValue = "SELECT_PLAN" | "CREDITS_CALCULATOR";

type AddCreditsModalProps = {
  showAddCreditsModal: boolean;
  setShowAddCreditsModal: (value: boolean) => void;
  selectedTab: TabsValue;
  setSelectedTab: (val: TabsValue) => void;
};

const AddCreditsModal: React.FC<AddCreditsModalProps> = ({
  showAddCreditsModal,
  setShowAddCreditsModal,
  selectedTab,
  setSelectedTab
}) => {
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [loading, setLoading] = useState(false);

  const { data, isLoading: loadingBundles } = useList({
    resource: "credits"
  });

  const { data: user, refetch: refetchUser } = useGetIdentity<{ discount: number }>();
  const { isDiscountedUser } = useUserType();

  const bundles = (data?.data || []) as Array<Bundle>;
  const discount = isDiscountedUser ? user?.discount || 0 : 0;

  return (
    <Modal
      width="480px"
      centered={selectedTab === "SELECT_PLAN"}
      open={showAddCreditsModal}
      destroyOnClose
      onCancel={() => {
        setSelectedTab("SELECT_PLAN");
        setShowAddCreditsModal(false);
      }}
      okButtonProps={{ style: { display: "none" } }}
      closeIcon={<img src={cancelIcon} alt="cancel-icon" style={{ padding: "3px" }} />}
      cancelButtonProps={{ size: "large" }}
      footer={null}
      styles={{
        content: {
          padding: 0,
          borderColor: "#202440",
          backgroundColor: "#202440",
          outline: "none"
        }
      }}>
      <Flex align="center">
        <Typography.Title
          level={3}
          style={{ width: "100%", textAlign: "center", margin: "40px 0 30px 0" }}>
          Add Credits
        </Typography.Title>
      </Flex>

      <Flex style={{ padding: "0 22px" }}>
        <Button
          className={`${classes.customTabs} ${selectedTab === "SELECT_PLAN" ? `${classes.selected}` : ""}`}
          onClick={() => {
            setSelectedTab("SELECT_PLAN");
          }}>
          Select plan
        </Button>
        <Button
          className={`${classes.customTabs} ${selectedTab === "CREDITS_CALCULATOR" ? `${classes.selected}` : ""}`}
          onClick={() => setSelectedTab("CREDITS_CALCULATOR")}>
          <Tooltip title={"Are you unsure how many credits you need to run you calculation?"}>
            Credits calculator
          </Tooltip>
        </Button>
      </Flex>
      {selectedTab === "SELECT_PLAN" && (
        <Card
          key={"SELECT_PLAN"}
          loading={loadingBundles}
          title={
            <div className={classes.titleContainer}>
              <span className={classes.title}>Seasonal offer</span>
              {discount > 0 && <span className={classes.discountBadge}>{discount}% discount</span>}
            </div>
          }
          style={{
            border: 0,
            borderRadius: "12px"
          }}
          styles={{
            header: {
              border: 0,
              padding: "24px",
              backgroundColor: "#202440"
            },
            body: {
              paddingLeft: "24px",
              paddingRight: "24px",
              paddingTop: 0,
              border: 0,
              overflowY: "auto",
              maxHeight: "500px",
              backgroundColor: "#202440"
            }
          }}>
          {(!bundles || !bundles.length) && <Empty description="There are no bundles to show." />}
          {bundles.map((bundle: Bundle, index) => {
            const originalAmount = currencyFormat.format(bundle.amount);
            const finalAmount =
              discount === 100
                ? "0"
                : currencyFormat.format(
                    Math.max(bundle.amount - bundle.amount * (discount / 100), 1)
                  );
            return (
              <Card
                key={bundle.id}
                onClick={() => {
                  setSelectedBundle((prevBundle) => (prevBundle?.id !== bundle.id ? bundle : null));
                }}
                style={{
                  marginBottom: 12,
                  borderColor: "#373B43"
                }}
                styles={{
                  header: {
                    backgroundColor: selectedBundle?.id === bundle.id ? "#FF5A174D" : "#202440",
                    cursor: "pointer",
                    borderBottomColor: "#373B43"
                  },
                  body: {
                    borderWidth: "1px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#202440",
                    borderTop: "1px solid #373B43"
                  }
                }}
                title={
                  <div className={classes.label}>
                    <Typography.Text
                      ellipsis={{
                        tooltip: bundle.description || `Bundle ${index + 1}`
                      }}
                      className={classes.labelText}
                      style={{
                        color: selectedBundle?.id === bundle.id ? "#FF5A17" : "#FFFFFF",
                        maxWidth: "240px"
                      }}>
                      {bundle.description || `Bundle ${index + 1}`}
                    </Typography.Text>
                    {selectedBundle?.id === bundle.id ? (
                      <div className={classes.checkboxChecked}>
                        <img src={checkIcon} alt="check-icon" />
                      </div>
                    ) : (
                      <div className={classes.checkbox} />
                    )}
                  </div>
                }
                type="inner">
                <Typography.Paragraph style={{ margin: 0 }}>
                  <span className={classes.amountText}>
                    {discount > 0 && (
                      <span className={classes.originalPrice}>{originalAmount}</span>
                    )}
                    {finalAmount}{" "}
                  </span>
                  <span className={classes.creditText}>for {bundle.credit} credits</span>
                  {finalAmount.substring(1) === "1" && (
                    <Tooltip title="If the discounted amount is less than €1, it will be rounded up to €1 due to payment processor limitations">
                      <InfoCircleOutlined style={{ marginLeft: 5, color: "#bfc2c8" }} />
                    </Tooltip>
                  )}
                </Typography.Paragraph>
              </Card>
            );
          })}

          <Alert
            type="info"
            showIcon
            style={{
              background: "transparent",
              color: "#fff",
              outline: "none"
            }}
            icon={
              <InfoCircleFilled
                style={{ color: "#E75012", fontSize: 18, marginTop: 3, alignSelf: "start" }}
              />
            }
            message={
              <Typography.Text>
                If you need a bigger bundle, API access or help in calculating how many credits you
                need, please contact us at{" "}
                <Typography.Link href={`mailto:${QUIX_SALES_EMAIL}`}>
                  {QUIX_SALES_EMAIL}
                </Typography.Link>
              </Typography.Text>
            }
          />

          <Flex gap={12} justify="center">
            <Button
              loading={loading}
              onClick={async () => {
                if (!selectedBundle) {
                  message.error("You have not selected any plan.");
                  return;
                }

                if (discount < 100) {
                  message.success("Redirecting to payment gateway");
                }

                setLoading(true);
                await createNewPayment({
                  bundle_id: selectedBundle.id,
                  creditId: selectedBundle.id,
                  ...selectedBundle
                });
                await refetchUser();
                setLoading(false);
              }}
              style={{
                backgroundColor: "#FF5A17",
                color: "#FFFFFF",
                borderColor: "#FF5A17",
                width: "100%",
                height: "44px",
                borderRadius: "8px",
                marginTop: 12,
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: "24px",
                padding: "10px 18px 10px 18px"
              }}>
              Confirm
            </Button>
          </Flex>
        </Card>
      )}
      {selectedTab === "CREDITS_CALCULATOR" && (
        <div>
          <CreditsCalculatorTab />
        </div>
      )}
    </Modal>
  );
};

export default AddCreditsModal;
